import { generateTestId } from "@client-shared";
import type { Installation } from "@shared/types";

/**
 * The system-wide fallback order prefix applied when a roaster has not
 * configured their own. Mirrors `defaultOrderPrefix` in the order handlers
 * (`shared/src/handlers/orders/shopify.ts` and `woocommerce.ts`).
 */
export const DEFAULT_ORDER_PREFIX = "#BEANZ_";

/** Maximum length accepted for a custom order prefix. */
export const ORDER_PREFIX_MAX_LENGTH = 50;

/** Maximum length accepted for a single order-level tag. */
export const ORDER_TAG_MAX_LENGTH = 40;

/** Sample order id used when previewing how a generated order name will read. */
export const ORDER_PREVIEW_SAMPLE_ID = "12345";

export const setOrderConfigurationTestId = (identifier: string): string => generateTestId("OrderConfiguration", identifier);

export const setOrderConfigurationContainerTestId = (): string => setOrderConfigurationTestId("container");

export const setOrderConfigurationCardTestId = (): string => setOrderConfigurationTestId("card");

export const setOrderConfigurationTitleTestId = (): string => setOrderConfigurationTestId("title");

export const setOrderConfigurationDescriptionTestId = (): string => setOrderConfigurationTestId("description");

export const setOrderConfigurationInstallationSelectTestId = (): string => setOrderConfigurationTestId("installation-select");

export const setOrderConfigurationPreviewTestId = (): string => setOrderConfigurationTestId("order-name-preview");

export const setOrderConfigurationSaveButtonTestId = (): string => setOrderConfigurationTestId("save-button");

export const setOrderConfigurationEmptyStateTestId = (): string => setOrderConfigurationTestId("empty-state");

export const setOrderConfigurationLoadingTestId = (): string => setOrderConfigurationTestId("loading");

export const setOrderConfigurationTagsInputTestId = (): string => setOrderConfigurationTestId("tags-input");

export const setOrderConfigurationTagsDraftInputTestId = (): string => setOrderConfigurationTestId("tags-draft-input");

export const setOrderConfigurationTagTestId = (tag: string): string => setOrderConfigurationTestId(`tag-${tag}`);

export const setOrderConfigurationRemoveTagButtonTestId = (tag: string): string => setOrderConfigurationTestId(`remove-tag-${tag}`);

/**
 * Trim each tag, drop empty entries, and de-duplicate while preserving the
 * order the roaster entered them. De-duplication is case-sensitive because
 * downstream systems (Shopify) treat tags as case-sensitive.
 */
export const normalizeOrderTags = (tags: readonly string[]): string[] => {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const tag of tags) {
    const trimmed = tag.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    normalized.push(trimmed);
  }
  return normalized;
};

/** Human-readable label for an installation in the storefront selector. */
export const getInstallationLabel = (installation: Installation): string =>
  installation.domain?.trim() || installation.identifier.trim() || installation.id;

/**
 * Pick the installation that should be selected by default: the first ACTIVE
 * installation, falling back to the first installation of any state.
 */
export const pickDefaultInstallationId = (installations: readonly Installation[]): string | undefined => {
  const active = installations.find((installation) => installation.state === "ACTIVE");
  return (active ?? installations[0])?.id;
};

/** Build the order-name preview a roaster will see for a given prefix. */
export const buildOrderNamePreview = (orderPrefix: string): string =>
  `${orderPrefix.trim() || DEFAULT_ORDER_PREFIX}${ORDER_PREVIEW_SAMPLE_ID}`;

/**
 * Whether the edited prefix/tags differ from what is currently persisted on
 * the installation, used to gate the Save button.
 */
export const hasOrderConfigChanges = (installation: Installation, orderPrefix: string, orderTags: readonly string[]): boolean => {
  const savedPrefix = (installation.prefs?.orderPrefix ?? "").trim();
  // Normalize both sides: legacy installations may hold untrimmed or
  // duplicate tags, which should not count as unsaved changes.
  const savedTags = normalizeOrderTags(installation.prefs?.orderTags ?? []);
  const nextTags = normalizeOrderTags(orderTags);
  if (orderPrefix.trim() !== savedPrefix) {
    return true;
  }
  if (nextTags.length !== savedTags.length) {
    return true;
  }
  return nextTags.some((tag, index) => tag !== savedTags[index]);
};

/**
 * Produce the updated installation to persist, applying the edited order
 * prefix and tags onto the existing `prefs`. An empty prefix is stored as
 * `undefined` so the order handlers fall back to {@link DEFAULT_ORDER_PREFIX}.
 */
export const buildUpdatedInstallation = (installation: Installation, orderPrefix: string, orderTags: readonly string[]): Installation => {
  const trimmedPrefix = orderPrefix.trim();
  return {
    ...installation,
    prefs: {
      ...installation.prefs,
      orderPrefix: trimmedPrefix || undefined,
      orderTags: normalizeOrderTags(orderTags),
    },
  };
};
